
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */


const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const CodeSpecimen = makeShortcode("CodeSpecimen");
const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<style>{`
  table:not([class]) {
     margin-top: 1rem;
     font-size: 0.875rem;
     cell-spacing: none;
     border-spacing: 0;
     border-collapse: collapse;
  }

  table:not([class]) tr:nth-child(2n) {
    background: #eee;
  }

  table:not([class]) td, table:not([class]) th {
    border-bottom: #ccc 1px solid;
    margin-top: 0;
    padding: 0.25rem 0.5rem;
  }

  table:not([class]) tr {
    margin-bottom: 0;
  }

  li > code {
    font-size: 0.875rem;
  }
`}</style>
<p>{`While usually you'll want to publish your chart, sometimes you might want to
simply preview it, without going through the publishing process. This could be
especially helpful to programmatically generate charts based on many different
configuration options. Visualize offers a way to preview charts without
publishing them, by using a custom API or iframe hash parameter.`}</p>
<h2>{`iframe preview via query parameters`}</h2>
<p><strong parentName="p">{`Demo:`}</strong>{` Visit `}<a href="/_preview" target="_blank"><inlineCode parentName="p">{`/_preview`}</inlineCode></a>{` to see a
page with two iframes containing chart previews. The first iframe gets the chart
state via hash parameters.`}</p>
<p>{`In order to get the parameters, you can use the `}<em parentName="p">{`Copy preview link`}</em>{` button found
in the editor or use the
`}<a parentName="p" {...{
        "href": "https://github.com/visualize-admin/visualization-tool/blob/main/app/utils/hash-utils.ts"
      }}>{`objectToHashString`}</a>{`
function.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<iframe
  id="chart"
  src="https://visualize.admin.ch/it/preview#hash-params"
  width="100%"
  height="500"
  frameborder="0"
/>
`}</code></pre>
<p><strong parentName="p">{`Note:`}</strong>{` Manually changing `}<inlineCode parentName="p">{`chartType`}</inlineCode>{` can lead to unexpected behavior and
potentially necessitates further adjustments. For example a change from
`}<inlineCode parentName="p">{`chartType=bar`}</inlineCode>{` to `}<inlineCode parentName="p">{`chartType=column`}</inlineCode>{` needs a swap in `}<inlineCode parentName="p">{`x`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{` fields.`}</p>
<h2>{`iframe preview via API`}</h2>
<p><strong parentName="p">{`Demo:`}</strong>{` Visit `}<a href="/_preview" target="_blank"><inlineCode parentName="p">{`/_preview`}</inlineCode></a>{` to see a
page with two iframes containing chart previews. The second iframe gets the
chart state from posting a message to the iframe window.`}</p>
<p>{`This method works by pointing an iframe to the `}<inlineCode parentName="p">{`/preview`}</inlineCode>{` page, and posting a
message with the chart state to the iframe window when ready.`}</p>
<CodeSpecimen lang="js" raw rawBody={`const iframe = document.getElementById("chart");
const handleMessage = (e) => {
  if (e.data?.type === "ready" && e.source === iframe.contentWindow) {
    iframe.contentWindow?.postMessage(configuratorState, "*");
    window.removeEventListener("message", handleMessage);
  }
};
window.addEventListener("message", handleMessage);
`} mdxType="CodeSpecimen" />
<h3>{`Controlling the language`}</h3>
<p>{`You can set the desired language for the chart preview by adding a `}<inlineCode parentName="p">{`/de`}</inlineCode>{`, `}<inlineCode parentName="p">{`/fr`}</inlineCode>{`,
`}<inlineCode parentName="p">{`/it`}</inlineCode>{` or `}<inlineCode parentName="p">{`/en`}</inlineCode>{` part to the iframe URL, like so:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<iframe
  id="chart"
  src="https://visualize.admin.ch/it/preview"
  width="100%"
  height="500"
  frameborder="0"
/>
`}</code></pre>
<h2>{`POST request`}</h2>
<p><strong parentName="p">{`Demo:`}</strong>{` Visit `}<a href="/_preview_post" target="_blank"><inlineCode parentName="p">{`/_preview_post`}</inlineCode></a>{` to
see a page with two buttons that open a new page with a chart preview after
clicking.`}</p>
<p>{`This method works by sending a POST request to `}<inlineCode parentName="p">{`/preview_post`}</inlineCode>{` page with chart
state when clicking on a form button. The `}<inlineCode parentName="p">{`/preview_post`}</inlineCode>{` page retrieves the
content of a request in `}<inlineCode parentName="p">{`getServerSideProps`}</inlineCode>{` and renders a preview of a chart.`}</p>
<p>{`It's important to only use one input inside a form (as we split the string by
`}<inlineCode parentName="p">{`=`}</inlineCode>{`).`}</p>
<CodeSpecimen lang="css" raw rawBody={`<form method="post" action="/preview_post" target="_blank">
  <input
    type="hidden"
    name="configuratorState"
    value={JSON.stringify(photovoltaikanlagenState)}
  />
  <input type="submit" value="☀️ Preview a Photovoltaikanlagen chart" />
</form>`} mdxType="CodeSpecimen" />
<h3>{`Controlling the language`}</h3>
<p>{`You can set the desired language for the chart preview by adding a `}<inlineCode parentName="p">{`/de`}</inlineCode>{`, `}<inlineCode parentName="p">{`/fr`}</inlineCode>{`,
`}<inlineCode parentName="p">{`/it`}</inlineCode>{` or `}<inlineCode parentName="p">{`/en`}</inlineCode>{` part to the form action parameter, like so:`}</p>
<pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<form method="post" action="/it/preview_post" target="_blank">
  <input
    type="hidden"
    name="configuratorState"
    value="{JSON.stringify(photovoltaikanlagenState)}"
  />
  <input type="submit" value="☀️ Preview a Photovoltaikanlagen chart" />
</form>
`}</code></pre>
<h2>{`Configurator and chart config schemas`}</h2>
<p>{`As the application constantly evolves, the configurator and chart config schemas
will change. You can find their latest TypeScript definitions in the
`}<a parentName="p" {...{
        "href": "https://github.com/visualize-admin/visualization-tool/blob/main/app/config-types.ts"
      }}>{`config-types.ts`}</a>{`
file and according JSON Schema definitions and examples in the
`}<a parentName="p" {...{
        "href": "https://github.com/visualize-admin/visualization-tool/tree/main/app/public/json-schema"
      }}>{`json-schema folder`}</a>{`.`}</p>
<p>{`JSON Schema examples can be opened e.g. in Visual Studio Code, where you will
have an autocomplete feature to help you fill in the configuration.`}</p>
<p>{`Note that the types are very complex and it's encouraged to visit the Visualize
application with `}<inlineCode parentName="p">{`&flag__debug=true`}</inlineCode>{` added to the end of the URL to enable the
debug mode. You will then see the configurator state directly below the chart
that is being edited. It also enables the `}<inlineCode parentName="p">{`Dump to console`}</inlineCode>{` button, which will
log the state to the browser console, for easier re-use.`}</p>
<p>{`An example configurator state is shown below.`}</p>
<CodeSpecimen lang="json" raw rawBody={`{
  state: "CONFIGURING_CHART",
  dataSet:
    "https://energy.ld.admin.ch/sfoe/bfe_ogd84_einmalverguetung_fuer_photovoltaikanlagen/7",
  dataSource: {
    type: "sparql",
    url: "https://cached.lindas.admin.ch/query",
  },
  meta: {
    title: {
      de: "",
      fr: "",
      it: "",
      en: "",
    },
    description: {
      de: "",
      fr: "",
      it: "",
      en: "",
    },
  },
  chartConfig: {
    version: "1.4.2",
    chartType: "column",
    filters: {
      "https://energy.ld.admin.ch/sfoe/bfe_ogd84_einmalverguetung_fuer_photovoltaikanlagen/Kanton":
        {
          type: "single",
          value: "https://ld.admin.ch/canton/1",
        },
    },
    interactiveFiltersConfig: {
      legend: {
        active: false,
        componentIri: "",
      },
      timeRange: {
        active: false,
        componentIri:
          "https://energy.ld.admin.ch/sfoe/bfe_ogd84_einmalverguetung_fuer_photovoltaikanlagen/Jahr",
        presets: {
          type: "range",
          from: "",
          to: "",
        },
      },
      dataFilters: {
        active: false,
        componentIris: [],
      },
      calculation: {
        active: false,
        type: "identity",
      },
    },
    fields: {
      x: {
        componentIri:
          "https://energy.ld.admin.ch/sfoe/bfe_ogd84_einmalverguetung_fuer_photovoltaikanlagen/Jahr",
        sorting: {
          sortingType: "byAuto",
          sortingOrder: "asc",
        },
      },
      y: {
        componentIri:
          "https://energy.ld.admin.ch/sfoe/bfe_ogd84_einmalverguetung_fuer_photovoltaikanlagen/AnzahlAnlagen",
      },
    },
  },
}`} mdxType="CodeSpecimen" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;